import { Check } from 'lucide-react';
import { TIER_META } from './vipTiers';

/**
 * Presentational card for a single Founding-100 tier.
 * Shows live slot count, pricing breakdown, perks, and opens the registration modal.
 */
export default function VIPTierCard({ tierKey, count, onReserve }) {
  const meta = TIER_META[tierKey];
  const spotsLeft = Math.max(0, (count?.total || meta.total) - (count?.claimed || 0));
  const isFull = spotsLeft === 0;
  const Icon = meta.icon;

  return (
    <div
      className="flex flex-col bg-[#0A0A0A] border border-[#FFD700]/30 rounded-2xl overflow-hidden hover:border-[#FFD700] transition-all shadow-[0_0_40px_rgba(255,215,0,0.08)]"
      data-testid={`vip-card-${tierKey}`}
    >
      <div className="p-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Icon className="w-6 h-6 text-[#FFD700]" />
            <h3 className="text-2xl font-bold text-white">{meta.label}</h3>
          </div>
          <span
            className={`text-xs font-bold px-3 py-1 rounded-full ${
              isFull
                ? 'bg-zinc-800 text-zinc-500'
                : 'bg-[#00D26A]/20 text-[#00D26A] border border-[#00D26A]/40'
            }`}
            data-testid={`vip-slots-${tierKey}`}
          >
            {isFull ? 'FULL' : `${spotsLeft} of ${meta.total} left`}
          </span>
        </div>

        <p className="text-zinc-400 text-sm mb-6">{meta.tagline}</p>

        <div className="mb-6">
          <div className="flex items-baseline gap-2">
            <span className="text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#00D26A] to-[#FFD700]">
              £{meta.vip}
            </span>
            <span className="text-zinc-400">/ 3 years</span>
          </div>
          <p className="text-sm text-zinc-500 mt-2">
            Regular price: <span className="line-through">£{meta.regular}</span>{' '}
            <span className="text-[#00D26A] font-semibold">· 90% off</span>{' '}
            <span className="text-zinc-600">(£{meta.monthly}/mo &times; 36)</span>
          </p>
        </div>

        <ul className="space-y-2 mb-8">
          {meta.perks.map((perk) => (
            <li key={perk} className="flex items-start gap-2 text-sm text-zinc-300">
              <Check className="w-4 h-4 text-[#00D26A] mt-0.5 flex-shrink-0" />
              <span>{perk}</span>
            </li>
          ))}
        </ul>

        <button
          onClick={() => onReserve(tierKey)}
          disabled={isFull}
          data-testid={`vip-cta-${tierKey}`}
          className={`w-full font-bold px-6 py-4 rounded-full text-lg transition-all ${
            isFull
              ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-[#00D26A] to-[#FFD700] text-black hover:opacity-90 transform hover:translate-y-[-2px] shadow-[0_0_30px_rgba(0,210,106,0.3)]'
          }`}
        >
          {isFull ? 'Spots Full' : `Reserve ${meta.label} Spot`}
        </button>
        <p className="text-xs text-zinc-500 text-center mt-3">
          Interest only — no payment now. Claim at launch.
        </p>
      </div>
    </div>
  );
}
